// src/context/HighlightContext.tsx
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import { getHighlights, Highlight, saveHighlights, useUser } from "../../hooks";
import { usePublication } from "../../hooks/usePublication";

export interface HighlightContextType {
  highlights: Highlight[];
  addHighlight: (h: Omit<Highlight, "id">) => void;
  updateHighlight: (id: string, data: Partial<Highlight>) => void;
  removeHighlight: (id: string) => void;
  loading: boolean;
  error: string | null;
}

const HighlightContext = createContext<HighlightContextType>({
  highlights: [],
  addHighlight: () => {},
  updateHighlight: () => {},
  removeHighlight: () => {},
  loading: false,
  error: null,
});

export const HighlightProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user } = useUser();
  const { idLibro } = useParams<{ idLibro?: string }>();
  const { publication } = usePublication();

  const [highlights, setHighlights] = useState<Highlight[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // keep a ref for debounced save
  const saveTimerRef = useRef<number | null>(null);
  const pendingSaveRef = useRef<boolean>(false);

  // token if any (adjust according to your auth)
  const token = (user as any)?.token || undefined;

  // Load highlights when userId and bookId ready
  useEffect(() => {
    const userId = (user as any)?.sus_id;
    const bookId = idLibro;
    if (!userId || !bookId) {
      setHighlights([]);
      return;
    }

    let mounted = true;
    setLoading(true);
    getHighlights(userId, bookId, token)
      .then((data) => {
        if (!mounted) return;
        setHighlights(data || []);
      })
      .catch((err) => {
        console.error("Error loading highlights:", err);
        if (mounted) setError(err.message || "Error cargando highlights");
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [user?.sus_id, idLibro]);

  // Helper to schedule save (debounced)
  const scheduleSave = (newHighlights: Highlight[]) => {
    const userId = (user as any)?.sus_id;
    const bookId = idLibro;
    const chapterId = publication.currentSection;
    if (!userId || !bookId) return;

    // Asegurar que todos tengan el chapterId correcto
    const enrichedHighlights = newHighlights.map((h) => {
  const effectiveChapterId =
    h.chapterId !== undefined && h.chapterId !== null && +h.chapterId !== 0
      ? h.chapterId
      : chapterId;

  return {
    ...h,
    chapterId: effectiveChapterId.toString(),
  };
});

    if (saveTimerRef.current) {
      window.clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }

    saveTimerRef.current = window.setTimeout(() => {
      pendingSaveRef.current = true;
      saveHighlights(
        {
          user_id: userId,
          book_id: bookId,
          chapter_id: chapterId.toString(),
          highlights: enrichedHighlights,
        },
        token
      )
        .catch((err) => {
          console.error("Error saving highlights:", err);
          setError(err.message || "Error al guardar highlights");
        })
        .finally(() => {
          pendingSaveRef.current = false;
        });
      saveTimerRef.current = null;
    }, 700);
  };

  // CRUD operations (optimistic updates)
  const addHighlight = (h: Omit<Highlight, "id">) => {
    // generate id client side (temporarily). Backend can keep same id or generate new; we expect the id format is uuid.
    const newId = crypto.randomUUID();
    const newH: Highlight = { ...h, id: newId};
    const updated = [...highlights, newH];
    setHighlights(updated);
    scheduleSave(updated);
  };

  const updateHighlight = (id: string, data: Partial<Highlight>) => {
    const updated = highlights.map((hl) =>
      hl.id === id ? { ...hl, ...data } : hl
    );
    setHighlights(updated);
    scheduleSave(updated);
  };

  const removeHighlight = (id: string) => {
    const updated = highlights.filter((hl) => hl.id !== id);
    setHighlights(updated);
    scheduleSave(updated);
  };

  // cleanup on unmount: flush pending save synchronously (best-effort)
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) {
        window.clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }
      const userId = (user as any)?.sus_id;
      const bookId = idLibro;
      const chapterId = publication.currentSection;
      if (userId && bookId && !pendingSaveRef.current) {
        // fire-and-forget (no await)
        saveHighlights(
          {
            user_id: userId,
            book_id: bookId,
            chapter_id: chapterId.toString(),
            highlights,
          },
          token
        ).catch((err) =>
          console.error("Error saving highlights on unmount:", err)
        );
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <HighlightContext.Provider
      value={{
        highlights,
        addHighlight,
        updateHighlight,
        removeHighlight,
        loading,
        error,
      }}
    >
      {children}
    </HighlightContext.Provider>
  );
};

export const useHighlight = () => useContext(HighlightContext);
export default HighlightContext;
